// src/pages/HousekeeperPortal.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
    Phone, Lock, LogOut, Loader2, Delete, ChevronRight, ChevronDown,
    CheckCircle2, ClipboardList, Undo2, Check, Clock, Building, AlertTriangle, RefreshCw, CalendarDays, User
} from 'lucide-react';
import { api } from '../api';
import toast from 'react-hot-toast';
import { toZonedTime, format } from 'date-fns-tz';
import '../styles/HousekeeperPortal.css'; 
import logo from '../assets/logo_AuthPages.png'; 
import ProfileModal from '../components/profile/ProfileModal';

const HousekeeperPortal = () => {
    // --- State ---
    const [step, setStep] = useState('LOADING'); 
    const [activeTab, setActiveTab] = useState('MY_LIST'); 
    const [loading, setLoading] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isPropertyDropdownOpen, setIsPropertyDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const propertyDropdownRef = useRef(null);
    
    // Clock In State
    const [isClockedIn, setIsClockedIn] = useState(true); 

    // Login Inputs
    const [phone, setPhone] = useState('');
    const [pin, setPin] = useState('');
    const [activeField, setActiveField] = useState('phone'); 
    
    // Session Data
    const [user, setUser] = useState(null);
    const [loginOptions, setLoginOptions] = useState([]); 
    
    // Housekeeping Data
    const [availableRooms, setAvailableRooms] = useState([]); 
    const [myRooms, setMyRooms] = useState([]); 
    const [selectedRoomIds, setSelectedRoomIds] = useState([]); 

    // --- NEW: Time Tracking State ---
    const [timeEntries, setTimeEntries] = useState([]);
    const [totalHours, setTotalHours] = useState('0h 0m');
    const [timeFilter, setTimeFilter] = useState('CURRENT_PAY_PERIOD');
    const [loadingTime, setLoadingTime] = useState(false);

    // --- 1. INITIALIZATION ---
    useEffect(() => {
        const checkSession = async () => {
            const storedUser = localStorage.getItem('hkUser');
            const storedToken = localStorage.getItem('hkToken');

            if (storedUser && storedToken) {
                const parsedUser = JSON.parse(storedUser);
                setUser(parsedUser);
                if (api.setTempToken) api.setTempToken(storedToken); 
                await loadData(parsedUser.property_id, parsedUser.id);
                setStep('DASHBOARD');
            } else {
                setStep('LOGIN');
            }
        };
        checkSession();
    }, []);

    // --- 2. KEYBOARD LISTENER ---
    useEffect(() => {
        if (step !== 'LOGIN') return;
        const handleKeyDown = (e) => {
            // Allow copy/paste shortcuts
            if (e.ctrlKey || e.metaKey) return;
            if (e.altKey) return;
            // Allow paste with Ctrl+V
            if (e.ctrlKey && e.key === 'v') return;
            if (/^[0-9]$/.test(e.key)) handleKeyPress(parseInt(e.key));
            else if (e.key === 'Backspace') handleBackspace();
            else if (e.key === 'Enter') {
                e.preventDefault();
                if (phone.length >= 10 && pin.length >= 4) handleLogin(phone, pin);
            } else if (e.key === 'Tab') {
                e.preventDefault();
                setActiveField(prev => prev === 'phone' ? 'pin' : 'phone');
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [step, activeField, phone, pin]);

    // Click outside handler for dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
            if (propertyDropdownRef.current && !propertyDropdownRef.current.contains(event.target)) {
                setIsPropertyDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // --- 3. DATA LOADING & CLOCK-IN CHECK ---
    const loadData = useCallback(async (propertyId, userId, isBackground = false) => {
        try {
            const data = await api.getHousekeepingBoard(propertyId);
            
            // CLOCK IN CHECK
            const activeStaff = data.housekeepers || [];
            const amIWorking = activeStaff.find(staff => staff.id === userId);
            
            if (!amIWorking) {
                setIsClockedIn(false);
            } else {
                setIsClockedIn(true);
            }

            // NORMAL DATA PROCESSING
            const allRooms = data.rooms || [];
            const available = allRooms.filter(r => r.board_status === 'PENDING' && !r.assignee_id);
            let mine = allRooms.filter(r => r.assignee_id === userId && ['PENDING', 'IN_PROGRESS', 'PENDING_INSPECTION'].includes(r.board_status));
            
            mine.sort((a, b) => {
                const score = (status) => {
                    if (status === 'IN_PROGRESS') return 1;
                    if (status === 'PENDING') return 2;
                    if (status === 'PENDING_INSPECTION') return 3;
                    return 4;
                };
                return score(a.board_status) - score(b.board_status);
            });

            setAvailableRooms(available);
            setMyRooms(mine);
            
        } catch (err) {
            console.error(err);
            if (!isBackground) toast.error("Could not refresh data");
        }
    }, []);

    // --- 4. AUTO-REFRESH ---
    useEffect(() => {
        if (step !== 'DASHBOARD' || !user || activeTab === 'MY_HOURS') return; // Don't refresh board if looking at hours
        const intervalId = setInterval(() => {
            loadData(user.property_id, user.id, true);
        }, 10000);
        return () => clearInterval(intervalId);
    }, [step, user, activeTab, loadData]);

    // --- 5. NEW: LOAD TIME RECORDS ---
    const getDatesForFilter = (filter) => {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        const date = now.getDate();
        
        const toISO = (d) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };

        if (filter === 'CURRENT_PAY_PERIOD') {
            if (date <= 15) return { start: toISO(new Date(year, month, 1)), end: toISO(new Date(year, month, 15)) };
            else return { start: toISO(new Date(year, month, 16)), end: toISO(new Date(year, month + 1, 0)) };
        } else if (filter === 'PREVIOUS_PAY_PERIOD') {
            if (date <= 15) return { start: toISO(new Date(year, month - 1, 16)), end: toISO(new Date(year, month, 0)) };
            else return { start: toISO(new Date(year, month, 1)), end: toISO(new Date(year, month, 15)) };
        }
        return { start: toISO(now), end: toISO(now) };
    };

    useEffect(() => {
        const fetchTimeRecords = async () => {
            if (activeTab !== 'MY_HOURS' || !user) return;
            setLoadingTime(true);
            try {
                const { start, end } = getDatesForFilter(timeFilter);
                const data = await api.getWorkSummary({
                    propertyId: user.property_id,
                    employeeId: user.id,
                    startDate: start,
                    endDate: end
                });

                setTimeEntries(data.punches || []);
                
                // Extract total hours from summary
                if (data.summary && Object.keys(data.summary).length > 0) {
                    const employeeSummary = Object.values(data.summary)[0];
                    setTotalHours(employeeSummary.total_hours || '0h 0m');
                } else {
                    setTotalHours('0h 0m');
                }
            } catch (err) {
                console.error(err);
                toast.error("Could not load time records");
            } finally {
                setLoadingTime(false);
            }
        };

        fetchTimeRecords();
    }, [activeTab, timeFilter, user]);

    // Helper to format time
    const formatTime = (isoString) => {
        if (!isoString) return '-';
        try {
            const tz = 'America/Los_Angeles'; // Default fallback
            const zonedDate = toZonedTime(isoString, tz);
            return format(zonedDate, 'hh:mm a', { timeZone: tz });
        } catch (e) {
            return '-';
        }
    };

    // --- ACTIONS: LOGIN ---
    const handleLogin = async (phoneInput, pinInput) => {
        if (loading) return;
        setLoading(true);
        const toastId = toast.loading("Verifying...");

        try {
            const response = await api.quickLogin({ phone: phoneInput, code: pinInput });
            toast.dismiss(toastId);

            const options = response.options || [];

            if (options.length === 0) {
                toast.error("No active accounts found.");
            } else if (options.length === 1) {
                finalizeLogin(options[0]);
            } else {
                setLoginOptions(options);
                setStep('PROPERTY_SELECT');
                toast.success("Select Property");
            }

        } catch (error) {
            toast.dismiss(toastId);
            toast.error(error.message || "Invalid Login");
            setPin(''); setActiveField('pin');
        } finally {
            setLoading(false);
        }
    };

    const finalizeLogin = async (option) => {
        const { user: userData, token } = option;
        toast.success(`Welcome, ${userData.first_name}!`);
        localStorage.setItem('hkToken', token);
        localStorage.setItem('hkUser', JSON.stringify(userData));
        if (api.setTempToken) api.setTempToken(token);
        setUser(userData);
        // Keep loginOptions for property switching (don't clear them)
        await loadData(userData.property_id, userData.id);
        setStep('DASHBOARD');
        setActiveTab('MY_LIST'); 
    };

    const handleLogout = () => {
        localStorage.removeItem('hkToken');
        localStorage.removeItem('hkUser');
        setStep('LOGIN'); setPhone(''); setPin(''); 
        setMyRooms([]); setAvailableRooms([]);
        setLoginOptions([]);
        setUser(null);
        setIsClockedIn(true); 
    };

    // --- PROPERTY SWITCHER ---
    const handlePropertySwitch = async (option) => {
        if (option.user.property_id === user.property_id) {
            setIsPropertyDropdownOpen(false);
            return; // Already on this property
        }
        
        setLoading(true);
        setIsPropertyDropdownOpen(false);
        
        try {
            // Update user data
            const newUserData = option.user;
            const newToken = option.token;
            
            localStorage.setItem('hkToken', newToken);
            localStorage.setItem('hkUser', JSON.stringify(newUserData));
            if (api.setTempToken) api.setTempToken(newToken);
            
            setUser(newUserData);
            await loadData(newUserData.property_id, newUserData.id);
            
            toast.success(`Switched to ${newUserData.property_name}`);
        } catch (error) {
            console.error('Property switch error:', error);
            toast.error('Failed to switch property');
        } finally {
            setLoading(false);
        }
    };

    // --- ACTIONS: TASKS ---
    const handleAssignYourself = async () => {
        if (selectedRoomIds.length === 0) return;
        setLoading(true);
        try {
            const assignPromises = selectedRoomIds.map(roomId => {
                const room = availableRooms.find(r => r.room_id === roomId);
                return api.updateTaskStatus({
                    taskId: room?.task_id, roomId: roomId, propertyId: user.property_id,
                    status: 'IN_PROGRESS', assignedUserId: user.id
                });
            });
            await Promise.all(assignPromises);
            toast.success("Assigned!");
            setSelectedRoomIds([]);
            await loadData(user.property_id, user.id);
            setActiveTab('MY_LIST'); 
        } catch (err) { toast.error("Failed to assign"); } finally { setLoading(false); }
    };

    const handleReturnToQueue = async (room) => {
        const toastId = toast.loading("Returning...");
        try {
            await api.updateTaskStatus({
                taskId: room.task_id, roomId: room.room_id, propertyId: user.property_id,
                status: 'PENDING', assignedUserId: null   
            });
            toast.dismiss(toastId);
            await loadData(user.property_id, user.id);
        } catch (err) { toast.dismiss(toastId); toast.error("Failed"); }
    };

    const handleMarkComplete = async (room) => {
        const toastId = toast.loading("Completing...");
        try {
            await api.updateTaskStatus({
                taskId: room.task_id, roomId: room.room_id, propertyId: user.property_id,
                status: 'PENDING_INSPECTION', assignedUserId: user.id
            });
            toast.dismiss(toastId);
            toast.success(`Room ${room.room_number} Done!`);
            await loadData(user.property_id, user.id);
        } catch (err) { toast.dismiss(toastId); toast.error("Failed"); }
    };

    const toggleRoomSelection = (roomId) => {
        setSelectedRoomIds(prev => prev.includes(roomId) ? prev.filter(id => id !== roomId) : [...prev, roomId]);
    };

    // --- KEYPAD ---
    const handleKeyPress = (num) => {
        if (activeField === 'phone') { if (phone.length < 10) setPhone(prev => prev + String(num)); } 
        else { 
            if (pin.length < 4) {
                const newPin = pin + String(num); setPin(newPin);
                if (newPin.length === 4 && phone.length >= 10) handleLogin(phone, newPin);
            }
        }
    };
    const handleBackspace = () => {
        if (activeField === 'pin') { if (pin.length > 0) setPin(prev => prev.slice(0, -1)); else setActiveField('phone'); } 
        else setPhone(prev => prev.slice(0, -1));
    };
    const getTodayDisplay = () => new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    // --- RENDER: LOADING ---
    if (step === 'LOADING') return <div className="hk-portal-container"><Loader2 className="spin"/></div>;

    // --- RENDER: LOGIN ---
    if (step === 'LOGIN') {
        return (
            <div className="hk-portal-container">
                <div className="hk-login-wrapper">
                    <div className="hk-logo-section">
                        <img src={logo} alt="Western Tech" />
                    </div>
                    <div className="hk-brand-header">
                        <h2>Housekeeping Portal</h2> 
                        <p>Enter your PIN to start</p>
                    </div>
                    <div className="hk-display-area">
                        <div className={`hk-display-field ${activeField === 'phone' ? 'active' : ''}`} onClick={() => setActiveField('phone')}>
                            <Phone size={18} className="field-icon"/>
                            <span className={phone ? 'text-filled' : 'text-empty'}>{phone || 'Phone Number'}</span>
                        </div>
                        <div className={`hk-display-field ${activeField === 'pin' ? 'active' : ''}`} onClick={() => setActiveField('pin')}>
                            <Lock size={18} className="field-icon"/>
                            <div className="pin-dots">
                                {[0, 1, 2, 3].map(i => <div key={i} className={`dot ${pin.length > i ? 'filled' : ''}`} />)}
                            </div>
                        </div>
                    </div>
                    <div className="hk-keypad">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => ( <button key={num} onClick={() => handleKeyPress(num)} className="key-btn">{num}</button> ))}
                        <div className="key-spacer"></div>
                        <button onClick={() => handleKeyPress(0)} className="key-btn">0</button>
                        <button onClick={handleBackspace} className="key-btn key-backspace"><Delete size={24} /></button>
                    </div>
                </div>
            </div>
        );
    }

    // --- RENDER: SELECTION SCREEN ---
    if (step === 'PROPERTY_SELECT') {
        return (
            <div className="hk-portal-container">
                <div className="hk-login-wrapper" style={{maxWidth:'450px'}}>
                    <div className="hk-logo-section">
                        <img src={logo} alt="Western Tech" />
                    </div>
                    <div className="hk-brand-header">
                        <h2>Select Property</h2>
                        <p>Where are you working today?</p>
                    </div>
                    <div style={{display:'flex', flexDirection:'column', gap:'12px', width:'100%'}}>
                        {loginOptions.map((opt, idx) => (
                            <button 
                                key={idx} 
                                className="hk-prop-select-btn"
                                onClick={() => finalizeLogin(opt)}
                            >
                                <Building size={20} />
                                <div style={{textAlign:'left'}}>
                                    <div style={{fontWeight:600}}>{opt.user.property_name}</div>
                                    <div style={{fontSize:'0.8rem', color:'#64748b'}}>ID: {opt.user.first_name} {opt.user.last_name}</div>
                                </div>
                                <ChevronRight size={18} style={{marginLeft:'auto', opacity:0.5}}/>
                            </button>
                        ))}
                    </div>
                    <button onClick={handleLogout} className="icon-btn-light" style={{marginTop:'20px'}}>
                        <LogOut size={20}/>
                    </button>
                </div>
            </div>
        );
    }

    // --- RENDER: DASHBOARD ---
    const userInitials = user ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() : '?';
    const hasMultipleProperties = loginOptions.length > 1;

    return (
        <div className="hk-portal-main">
            <header className="portal-header">
                <div>
                    <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                        <h1 style={{fontSize:'1.3rem'}}>{user?.first_name}</h1>
                        <span style={{fontSize:'0.85rem', color:'#64748b', fontWeight:500}}>
                            • {getTodayDisplay()}
                        </span>
                    </div>
                    {/* Property Switcher */}
                    <div style={{position:'relative', display:'inline-block'}} ref={propertyDropdownRef}>
                        <button 
                            onClick={() => hasMultipleProperties && setIsPropertyDropdownOpen(!isPropertyDropdownOpen)}
                            style={{
                                background:'none', border:'none', padding:'4px 8px', margin:'4px 0',
                                display:'flex', alignItems:'center', gap:'6px', cursor: hasMultipleProperties ? 'pointer' : 'default',
                                fontSize:'0.85rem', color:'#1798c1', fontWeight:600, borderRadius:'6px',
                                transition:'background 0.2s'
                            }}
                            onMouseEnter={(e) => hasMultipleProperties && (e.currentTarget.style.background='#f1f5f9')}
                            onMouseLeave={(e) => hasMultipleProperties && (e.currentTarget.style.background='none')}
                        >
                            <Building size={14} />
                            <span>{user?.property_name || 'Property'}</span>
                            {hasMultipleProperties && <ChevronDown size={14} style={{opacity:0.6}} />}
                        </button>
                        
                        {/* Property Dropdown */}
                        {isPropertyDropdownOpen && hasMultipleProperties && (
                            <div style={{
                                position:'absolute', top:'100%', left:0, marginTop:'4px',
                                background:'white', borderRadius:'12px', boxShadow:'0 4px 20px rgba(0,0,0,0.15)',
                                minWidth:'220px', zIndex:100, overflow:'hidden', border:'1px solid #e2e8f0'
                            }}>
                                <div style={{padding:'12px 16px', borderBottom:'1px solid #e2e8f0', fontSize:'0.75rem', color:'#64748b', fontWeight:600, textTransform:'uppercase'}}>
                                    Switch Property
                                </div>
                                {loginOptions.map((opt, idx) => {
                                    const isActive = opt.user.property_id === user?.property_id;
                                    return (
                                        <button
                                            key={idx}
                                            onClick={() => handlePropertySwitch(opt)}
                                            style={{
                                                width:'100%', padding:'12px 16px', border:'none', background: isActive ? '#f0f9ff' : 'white',
                                                display:'flex', alignItems:'center', gap:'12px', cursor:'pointer',
                                                textAlign:'left', transition:'background 0.2s'
                                            }}
                                            onMouseEnter={(e) => !isActive && (e.currentTarget.style.background='#f8fafc')}
                                            onMouseLeave={(e) => !isActive && (e.currentTarget.style.background='white')}
                                        >
                                            <div style={{
                                                width:'32px', height:'32px', borderRadius:'8px',
                                                background: isActive ? 'linear-gradient(135deg, #1798c1 0%, #0ea5e9 100%)' : '#f1f5f9',
                                                display:'flex', alignItems:'center', justifyContent:'center',
                                                color: isActive ? 'white' : '#64748b', fontSize:'0.75rem', fontWeight:600
                                            }}>
                                                {opt.user.property_name?.charAt(0) || 'P'}
                                            </div>
                                            <div style={{flex:1}}>
                                                <div style={{fontWeight: isActive ? 600 : 500, color: isActive ? '#0f172a' : '#334155', fontSize:'0.9rem'}}>
                                                    {opt.user.property_name}
                                                </div>
                                                <div style={{fontSize:'0.75rem', color:'#64748b'}}>
                                                    {opt.user.first_name} {opt.user.last_name}
                                                </div>
                                            </div>
                                            {isActive && <Check size={16} color="#1798c1" />}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <p>{myRooms.filter(r => r.board_status !== 'PENDING_INSPECTION').length} active • {availableRooms.length} queue</p>
                </div>
                <div className="portal-user-profile" ref={dropdownRef}>
                    <div className="portal-avatar" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                        {userInitials}
                    </div>
                    {isDropdownOpen && (
                        <ul className="portal-user-dropdown">
                            <li>
                                <button onClick={() => { setIsDropdownOpen(false); setIsProfileOpen(true); }} className="dropdown-item">
                                    <User size={16} />
                                    <span>Profile</span>
                                </button>
                            </li>
                            <li>
                                <button onClick={handleLogout} className="dropdown-item">
                                    <LogOut size={16} />
                                    <span>Logout</span>
                                </button>
                            </li>
                        </ul>
                    )}
                </div>
            </header>

            {/* --- UPDATED: 2 TABS --- */}
            <div className="portal-tabs" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                <button className={`portal-tab ${activeTab === 'MY_LIST' ? 'active' : ''}`} onClick={() => setActiveTab('MY_LIST')}>
                    <ClipboardList size={16} /> Tasks
                </button>
                <button className={`portal-tab ${activeTab === 'MY_HOURS' ? 'active' : ''}`} onClick={() => setActiveTab('MY_HOURS')}>
                    <Clock size={16} /> My Hours
                </button>
            </div>

            {/* --- CLOCK IN BLOCKER (Only hides Tasks, allows viewing Hours) --- */}
            {!isClockedIn && activeTab === 'MY_LIST' ? (
                <div className="portal-grid" style={{display:'flex', alignItems:'center', justifyContent:'center', height:'100%'}}>
                    <div className="empty-msg" style={{marginTop:0}}>
                        <div className="empty-icon">
                            <AlertTriangle size={64} color="#f59e0b" />
                        </div>
                        <h3 style={{color:'#f59e0b', fontSize:'1.5rem'}}>Not Clocked In</h3>
                        <p style={{maxWidth:'300px', margin:'10px auto'}}>
                            You must start your shift on the Time Clock before you can view or accept tasks.
                        </p>
                        <button className="confirm-btn" style={{marginTop:'20px', width:'auto'}} onClick={() => loadData(user.property_id, user.id)}>
                            I've Clocked In <RefreshCw size={16} /> 
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {/* --- TAB: TASKS (COMBINED MY LIST + QUEUE) --- */}
                    {activeTab === 'MY_LIST' && (
                        <div style={{paddingBottom: selectedRoomIds.length > 0 ? '80px' : '0'}}>
                            {/* My Assigned Rooms */}
                            <div style={{padding:'0 16px', marginBottom: myRooms.length > 0 ? '20px' : '0'}}>
                                {myRooms.length > 0 && (
                                    <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px'}}>
                                        <h3 style={{margin:0, fontSize:'0.85rem', fontWeight:600, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.5px'}}>
                                            My Tasks ({myRooms.length})
                                        </h3>
                                        <span style={{fontSize:'0.75rem', color:'#1798c1', fontWeight:500}}>
                                            {myRooms.filter(r => r.board_status !== 'PENDING_INSPECTION').length} active
                                        </span>
                                    </div>
                                )}
                                <div className="portal-grid" style={{padding:0}}>
                                    {myRooms.length === 0 ? (
                                        <div className="empty-msg" style={{padding:'20px 0'}}>
                                            <div className="empty-icon" style={{fontSize:'2rem'}}>📋</div>
                                            <h3 style={{fontSize:'1rem', margin:'8px 0 4px'}}>No tasks assigned</h3>
                                            <p style={{fontSize:'0.85rem', color:'#64748b'}}>Pick rooms from the queue below</p>
                                        </div>
                                    ) : (
                                        myRooms.map(room => {
                                            const isDone = room.board_status === 'PENDING_INSPECTION';
                                            return (
                                                <div key={room.room_id} className={`portal-card task-card ${isDone ? 'done' : ''}`}>
                                                    <div className="card-top-row">
                                                        <span className="p-room-num">{room.room_number}</span>
                                                        <span className={`p-status-mini ${isDone ? 'done' : ''}`}>
                                                            {isDone ? 'In Review' : 'Working'}
                                                        </span>
                                                    </div>
                                                    <div className="p-details-compact">
                                                        <span className="p-info-text">{room.room_type}</span>
                                                        <span className="p-info-text">{room.floor_name || 'Ground'}</span>
                                                    </div>
                                                    
                                                    {!isDone && (
                                                        <div className="task-actions">
                                                            <button className="btn-mini undo" onClick={() => handleReturnToQueue(room)}>
                                                                <Undo2 size={14}/> Return
                                                            </button>
                                                            <button className="btn-mini primary" onClick={() => handleMarkComplete(room)}>
                                                                <Check size={14}/> Done
                                                            </button>
                                                        </div>
                                                    )}
                                                    {isDone && (
                                                        <div className="task-actions">
                                                             <div className="btn-mini" style={{border:'none', background:'none', justifyContent:'flex-start', paddingLeft:0}}>
                                                                <Clock size={12}/> Waiting for Manager
                                                             </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>

                            {/* Divider */}
                            {myRooms.length > 0 && availableRooms.length > 0 && (
                                <div style={{padding:'0 16px', marginBottom:'16px'}}>
                                    <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                                        <div style={{flex:1, height:'1px', background:'#e2e8f0'}}></div>
                                        <span style={{fontSize:'0.75rem', color:'#94a3b8', fontWeight:500}}>AVAILABLE</span>
                                        <div style={{flex:1, height:'1px', background:'#e2e8f0'}}></div>
                                    </div>
                                </div>
                            )}

                            {/* Available Rooms (Queue) */}
                            <div style={{padding:'0 16px'}}>
                                {availableRooms.length > 0 && (
                                    <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px'}}>
                                        <h3 style={{margin:0, fontSize:'0.85rem', fontWeight:600, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.5px'}}>
                                            Queue ({availableRooms.length})
                                        </h3>
                                    </div>
                                )}
                                <div className="portal-grid" style={{padding:0}}>
                                    {availableRooms.length === 0 ? (
                                        myRooms.length > 0 ? null : (
                                            <div className="empty-msg" style={{padding:'20px 0'}}>
                                                <div className="empty-icon" style={{fontSize:'2rem'}}>🎉</div>
                                                <h3 style={{fontSize:'1rem', margin:'8px 0 4px'}}>Queue Empty</h3>
                                                <p style={{fontSize:'0.85rem', color:'#64748b'}}>All rooms have been assigned</p>
                                            </div>
                                        )
                                    ) : (
                                        availableRooms.map(room => {
                                            const isSelected = selectedRoomIds.includes(room.room_id);
                                            return (
                                                <div key={room.room_id} className={`portal-card ${isSelected ? 'selected' : ''}`} onClick={() => toggleRoomSelection(room.room_id)}>
                                                    <div className="card-top-row">
                                                        <span className="p-room-num">{room.room_number}</span>
                                                        {isSelected ? <CheckCircle2 className="p-check" /> : <div className="p-circle" />}
                                                    </div>
                                                    <div className="p-details-compact">
                                                        <span className="p-info-text">{room.room_type}</span>
                                                        <span className="p-info-text">{room.floor_name || 'Ground'}</span>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>

                            {/* Floating Assign Button */}
                            {selectedRoomIds.length > 0 && (
                                <div style={{
                                    position:'fixed', bottom:'80px', left:'16px', right:'16px',
                                    background:'white', borderRadius:'16px', padding:'16px',
                                    boxShadow:'0 -4px 20px rgba(0,0,0,0.15)',
                                    display:'flex', alignItems:'center', justifyContent:'space-between',
                                    zIndex:50
                                }}>
                                    <span style={{fontWeight:'700', color:'#0f172a', fontSize:'0.95rem'}}>
                                        {selectedRoomIds.length} Selected
                                    </span>
                                    <button className="confirm-btn" disabled={loading} onClick={handleAssignYourself} style={{margin:0}}>
                                        Assign Me <ChevronRight size={16} />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* --- TAB: MY HOURS (SELF SERVICE) --- */}
                    {activeTab === 'MY_HOURS' && (
                        <div className="portal-list">
                            
                            {/* Filter Bar */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', padding: '0 4px' }}>
                                <select 
                                    value={timeFilter} 
                                    onChange={(e) => setTimeFilter(e.target.value)}
                                    className="portal-filter-select"
                                >
                                    <option value="CURRENT_PAY_PERIOD">This Pay Period</option>
                                    <option value="PREVIOUS_PAY_PERIOD">Last Pay Period</option>
                                </select>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Total Hours</div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: '800', color: '#1798c1', lineHeight: '1' }}>{totalHours}</div>
                                </div>
                            </div>

                            {loadingTime ? (
                                <div style={{textAlign:'center', padding:'40px'}}><Loader2 className="spin" size={32} color="#1798c1" style={{margin:'auto'}}/></div>
                            ) : timeEntries.length === 0 ? (
                                <div className="empty-msg"><div className="empty-icon">📝</div><h3>No Records</h3><p>You have no recorded punches for this period.</p></div>
                            ) : (
                                timeEntries.map((entry, idx) => {
                                    // Identify VOIDED shifts
                                    const isCancelled = entry.start_time === entry.end_time;
                                    
                                    return (
                                        <div key={entry.id || idx} className={`portal-card ${isCancelled ? 'cancelled' : ''}`} style={{ opacity: isCancelled ? 0.7 : 1, padding: '16px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: '#1e293b', fontSize: '1.05rem' }}>
                                                    <CalendarDays size={18} color="#64748b" />
                                                    {entry.date}
                                                </div>
                                                {isCancelled ? (
                                                    <span className="p-status-mini" style={{background:'#fee2e2', color:'#991b1b'}}>Voided</span>
                                                ) : (
                                                    <span className="p-status-mini" style={{background: entry.status === 'Completed' ? '#dcfce7' : '#e0f2fe', color: entry.status === 'Completed' ? '#166534' : '#0369a1'}}>
                                                        {entry.status}
                                                    </span>
                                                )}
                                            </div>
                                            
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.95rem', color: '#475569' }}>
                                                <span style={{ textDecoration: isCancelled ? 'line-through' : 'none' }}>
                                                    {formatTime(entry.start_time)} <span style={{color: '#94a3b8', margin: '0 4px'}}>→</span> {entry.end_time ? formatTime(entry.end_time) : 'Now'}
                                                </span>
                                                <span style={{ fontWeight: '700', color: isCancelled ? '#991b1b' : '#334155', fontSize: '1rem' }}>
                                                    {isCancelled ? '0h 0m' : entry.duration}
                                                </span>
                                            </div>
                                            
                                            {/* Show manager notes if they voided/edited it */}
                                            {entry.notes && (
                                                <div style={{ marginTop: '12px', fontSize: '0.85rem', color: '#64748b', backgroundColor: '#f8fafc', padding: '10px', borderRadius: '8px', fontStyle: 'italic', border: '1px solid #e2e8f0' }}>
                                                    <span style={{fontWeight:'600'}}>Note:</span> {entry.notes}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}
                </>
            )}

            <ProfileModal 
                isOpen={isProfileOpen} 
                onClose={() => setIsProfileOpen(false)} 
            />
        </div>
    );
};

export default HousekeeperPortal;